import React, { useEffect, useMemo, useState } from 'react';

import Button from '@/components/common/Elements/Button/Button';
import Checkbox from '@/components/common/Elements/Checkbox/Checkbox';
import Icon, { ICONS } from '@/components/common/Elements/Icon/Icon';
import ModalFullscreenLayout from '@/components/common/Modal/ModalFullscreenLayout';
import ModalPortal from '@/components/common/Modal/ModalPortal';
import { useToggle } from '@/hooks/useToggle';

import { useGetLegalConsent } from '../api/getLegalConsent';
import { useUpdateLegalConsent } from '../api/updateLegalConsent';

const LegalConsentAcceptanceModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckedTermsAndConditions, toggleIsCheckedTermsAndConditions] =
    useToggle(false);
  const [isCheckedPrivacyPolicy, toggleIsCheckedPrivacyPolicy] =
    useToggle(false);
  const [isCheckedNewsLetter, toggleIsCheckedNewsLetter] = useToggle(false);

  const getLegalConsentQuery = useGetLegalConsent({});
  const updateLegalConsentMutation = useUpdateLegalConsent();

  const { termsAndConditions, privacyPolicy, newsletter } = useMemo(() => {
    return getLegalConsentQuery.data.consents;
  }, [getLegalConsentQuery.data]);

  useEffect(() => {
    toggleIsCheckedTermsAndConditions(termsAndConditions);
    toggleIsCheckedPrivacyPolicy(privacyPolicy);
    toggleIsCheckedNewsLetter(newsletter);

    if (!termsAndConditions || !privacyPolicy) {
      setIsModalOpen(true);
    }
  }, [
    termsAndConditions,
    privacyPolicy,
    newsletter,
    toggleIsCheckedTermsAndConditions,
    toggleIsCheckedPrivacyPolicy,
    toggleIsCheckedNewsLetter,
  ]);

  const handleClickAgree = async () => {
    if (!isCheckedTermsAndConditions || !isCheckedPrivacyPolicy) return;

    const params = {
      consents: {},
    };

    if (!termsAndConditions) {
      params.consents['termsAndConditions'] = isCheckedTermsAndConditions;
    }

    if (!privacyPolicy) {
      params.consents['privacyPolicy'] = isCheckedPrivacyPolicy;
    }

    if (!newsletter) {
      params.consents['newsletter'] = isCheckedNewsLetter;
    }

    await updateLegalConsentMutation.mutateAsync(params);

    window.location.href = '/';
  };

  if (!isModalOpen) {
    return null;
  }

  return (
    <ModalPortal>
      <ModalFullscreenLayout>
        <div
          className="tw-bg-white tw-rounded-[10px]"
          style={{ width: '1000px' }}
        >
          <div className="tw-flex tw-items-center tw-justify-center tw-h-[96px] tw-gap-[10px]">
            <Icon icon={ICONS.company_logo()} />
            <span className="tw-text-[24px] tw-text-cfo-blue-600 tw-font-brandonTextMedium">
              WebApp Account Policies Updated
            </span>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-[20px] tw-px-[40px] tw-py-[32px] tw-font-brandonTextMedium tw-border-y tw-border-cfo-grey-500">
            <div className="tw-text-[18px]">
              To continue, please take a moment to read and agree to our updated{' '}
              {!termsAndConditions && (
                <a
                  href={`/account#/legal-information/terms-and-conditions`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-text-cfo-blue-600 tw-underline hover:tw-text-cfo-blue-600"
                >
                  Customer and User Terms and Conditions
                </a>
              )}
              {!termsAndConditions && !privacyPolicy && ` and `}
              {!privacyPolicy && (
                <a
                  href={`/account#/legal-information/privacy-policy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-text-cfo-blue-600 tw-underline hover:tw-text-cfo-blue-600"
                >
                  Privacy Policy
                </a>
              )}
              .
            </div>
            <div className="tw-p-[20px] tw-flex tw-flex-col tw-gap-[20px] tw-text-[16px] tw-border tw-border-cfo-grey-500">
              {!termsAndConditions && (
                <div className="tw-flex tw-items-center tw-gap-[5px]">
                  <Checkbox
                    checked={isCheckedTermsAndConditions}
                    onChange={toggleIsCheckedTermsAndConditions}
                    checkboxSize="15px"
                  />
                  <span>Customer and User Terms and Conditions</span>
                </div>
              )}
              {!privacyPolicy && (
                <div className="tw-flex tw-items-center tw-gap-[5px]">
                  <Checkbox
                    checked={isCheckedPrivacyPolicy}
                    onChange={toggleIsCheckedPrivacyPolicy}
                    checkboxSize="15px"
                  />
                  <span>Privacy Policy</span>
                </div>
              )}
            </div>
            {!newsletter && (
              <div>
                <div className="tw-text-[18px] tw-mb-[10px]">
                  Check out the following option/s:
                </div>
                <div className="tw-p-[20px] tw-flex tw-flex-col tw-gap-[20px] tw-text-[16px] tw-border tw-border-cfo-grey-500">
                  <div className="tw-flex tw-items-center tw-gap-[5px]">
                    <Checkbox
                      checked={isCheckedNewsLetter}
                      onChange={toggleIsCheckedNewsLetter}
                      checkboxSize="15px"
                    />
                    <span>Subscribe to our newsletters (optional)</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className="tw-flex tw-items-center tw-justify-end tw-px-[40px]"
            style={{ height: '82px' }}
          >
            <Button
              onClick={handleClickAgree}
              buttonClassSettings={{ variant: 'secondary', size: 'lg' }}
              disabled={!isCheckedTermsAndConditions || !isCheckedPrivacyPolicy}
              className="disabled:tw-bg-cfo-grey-100 disabled:tw-border disabled:tw-border-cfo-grey-500"
            >
              Agree
            </Button>
          </div>
        </div>
      </ModalFullscreenLayout>
    </ModalPortal>
  );
};

export default LegalConsentAcceptanceModal;
